import { computed, ref } from 'vue'
import axios, { type AxiosProgressEvent } from 'axios'
import { useMutation } from '@vue/apollo-composable'

import { graphql } from '@/shared/graphql/router'
import {
  type FileType,
  type File as GqlFile,
} from '@/shared/graphql/router/graphql'
import { useInfringementProcessingStore } from '@/features/infringement/store'
import { useUserStore } from '@/shared/stores/user'
import { getFileType } from '@/shared/utils/file'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { openDialog, closeDialog, Dialog } from '@/shared/dialogs/dialog'
import { openModal, Modals } from '@/shared/dialogs/modals'

const UPLOAD_INFRINGING_FILE_MUTATION = graphql(`
  mutation UploadInfringingFile($input: UploadInfringingFileInput!) {
    uploadInfringingFile(input: $input) {
      id
      url
      headers
      error {
        code
        message
      }
    }
  }
`)

export const useCreateReport = () => {
  const userStore = useUserStore()
  const processingStore = useInfringementProcessingStore()

  const { mutate: uploadInfringingFile } = useMutation(
    UPLOAD_INFRINGING_FILE_MUTATION,
    { clientId: 'router' }
  )

  const file = ref<File>()
  const copyright = ref<GqlFile>()
  const dragging = ref(false)
  const uploading = ref(false)
  const error = ref(false)
  const percentage = ref(0)

  const ready = computed(() => !!file.value && !!copyright.value)

  const copyrightThumb = computed(() =>
    applyAuthorizationToken(copyright.value?.asset?.previewImageUrl, [
      { key: 'width', value: 72 },
    ])
  )

  const pickedType = computed(() =>
    file.value
      ? fileTypeToThumbnail(getFileType(file.value.name, file.value.type))
      : 'generic'
  )

  const handleSelect = () => {
    openDialog(Dialog.SelectCopyright, {
      stack: true,
      events: {
        success: (item) => {
          copyright.value = item as GqlFile
        },
      },
    })
  }

  const handleDrop = (event: DragEvent) => {
    dragging.value = false
    const dropped = event.dataTransfer?.files
    if (dropped?.length) {
      file.value = dropped[0]
      error.value = false
    }
  }

  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    if (target.files?.length) {
      file.value = target.files[0]
      error.value = false
    }
  }

  const handleRemove = () => {
    openModal(Modals.RemoveInfringingFile, {
      stack: true,
      events: {
        success: () => {
          file.value = undefined
          error.value = false
        },
      },
    })
  }

  const handleClose = () => {
    if (file.value) {
      return openModal(Modals.LeaveReportCreation)
    }
    closeDialog()
  }

  const handleCreate = async () => {
    if (!file.value || !copyright.value) return

    uploading.value = true
    error.value = false
    percentage.value = 0

    try {
      const response = await uploadInfringingFile({
        input: { copyrightId: copyright.value.id, filename: file.value.name },
      })
      const data = response?.data?.uploadInfringingFile

      if (!data?.url || !data?.id) {
        throw new Error(data?.error?.message || 'Failed to get upload URL')
      }

      const headers = JSON.parse(data.headers) as Record<string, string>
      const formData = new FormData()
      Object.entries(headers)
        .filter(([, value]) => !!value)
        .forEach(([key, value]) =>
          formData.append(key.replace(/_/g, '-'), value)
        )
      formData.append('file', file.value)

      await axios.post(data.url, formData, {
        onUploadProgress: (event: AxiosProgressEvent) => {
          percentage.value = Math.round((event.progress || 0) * 100)
        },
      })

      processingStore.addUploadedProcessingReport({
        copyright: copyright.value,
        infringingFile: {
          id: data.id,
          name: file.value.name,
          type: getFileType(
            file.value.name,
            file.value.type
          ) as unknown as FileType,
        },
      })
      userStore.updateUser({ isCreatingReport: true })
      closeDialog()
    } catch (err) {
      console.error('[useCreateReport]', err)
      uploading.value = false
      error.value = true
      percentage.value = 0
    }
  }

  return {
    file,
    copyright,
    dragging,
    uploading,
    error,
    percentage,
    ready,
    copyrightThumb,
    pickedType,
    handleSelect,
    handleDrop,
    handleInput,
    handleRemove,
    handleClose,
    handleCreate,
  }
}
